/**
 * Public Campus Header Component
 * 
 * Displays school logo, name, and navigation controls for public campus summary page.
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TimelineIcon from '@mui/icons-material/Timeline';

const DRIVE_TIMES = [5, 10, 15];

const TAB_CONFIGS = [
  {
    label: 'Population Trends',
    icon: <TrendingUpIcon />
  },
  {
    label: 'Area School Comparison',
    icon: <CompareArrowsIcon />
  },
  {
    label: 'Projected Enrollment',
    icon: <TimelineIcon />
  }
];

const PublicCampusHeader = ({
  campusInfo,
  selectedDriveTime,
  selectedTab,
  onDriveTimeChange,
  onTabChange
}) => {
  if (!campusInfo) return null;

  const { school, team } = campusInfo;

  return (
    <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg">
        {/* School Logo and Name Section */}
        <Box sx={{ pt: 4, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* Logo */}
            {team.logo_url ? (
              <Box
                component="img"
                src={team.logo_url}
                alt={`${school.name} logo`}
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                  borderRadius: 2,
                  bgcolor: 'white',
                  p: 1,
                  boxShadow: 1
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 1
                }}
              >
                <SchoolIcon sx={{ fontSize: 72, color: 'white' }} />
              </Box>
            )}
            
            {/* School Name and Location */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 0.5
                }}
              >
                {school.name}
              </Typography>
              {school.city && school.state && (
                <Typography variant="body1" color="text.secondary">
                  {school.city}, {school.state}
                </Typography>
              )}
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'inline-block',
                  mt: 1,
                  px: 2,
                  py: 0.5,
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: 1,
                  fontWeight: 600
                }}
              >
                School Explorer
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Controls Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pb: 2 }}>
          {/* Drive Time Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Drive Time</InputLabel>
            <Select
              value={selectedDriveTime}
              label="Drive Time"
              onChange={onDriveTimeChange}
              sx={{ bgcolor: 'white' }}
            >
              {DRIVE_TIMES.map((time) => (
                <MenuItem key={time} value={time}>
                  {time} minutes
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Adjust drive time to explore different geographic areas
          </Typography>
        </Box>

        {/* Tabs Navigation */}
        <Tabs
          value={selectedTab}
          onChange={onTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
        >
          {TAB_CONFIGS.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Container>
    </Box>
  );
};

export default PublicCampusHeader;

